import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../css/ParticipatingOrganisationList.css";
import "../css/ParticipantList.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faTrash, faPen, faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";

const ParticipatingOrganisationsList = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [competitionId, setCompetitionId] = useState("");
  const [listContext, setListContext] = useState("");
  const [listError, setListError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  /** Bumps when a new fetch or a successful delete runs — stale fetches must not call setRows. */
  const fetchGenRef = useRef(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const resolveActiveCompetitionId = async () => {
    try {
      const activeRes = await axios.get(`${API_URL}/ems/competitions/active`);
      if (activeRes?.data?._id) return String(activeRes.data._id);
    } catch (err) {
      console.warn("Fallback to /ems/competitions?active=true:", err);
    }
    const legacyRes = await axios.get(`${API_URL}/ems/competitions?active=true`);
    const arr = Array.isArray(legacyRes?.data) ? legacyRes.data : [];
    return arr[0]?._id ? String(arr[0]._id) : "";
  };

  const resolveLa2028CompetitionId = async () => {
    try {
      const laRes = await axios.get(`${API_URL}/ems/competitions/la-2028`);
      return laRes?.data?._id ? String(laRes.data._id) : "";
    } catch (err) {
      console.warn("Could not resolve LA 2028 fallback:", err);
      return "";
    }
  };

  const fetchOlympicDetails = async (search = "") => {
    const res = await axios.get(`${API_URL}/ems/olympic-games-2028/organisations`, {
      params: { search: search || undefined },
    });
    return Array.isArray(res.data) ? res.data : [];
  };

  const fetchRows = async (search = "") => {
    const gen = ++fetchGenRef.current;
    setListError("");
    setListContext("");
    setLoading(true);
    try {
      let cid = competitionId || (await resolveActiveCompetitionId());
      if (!cid) {
        if (gen === fetchGenRef.current) {
          setRows([]);
          setListError("No active competition found.");
        }
        return;
      }
      setCompetitionId(cid);

      let res = await axios.get(`${API_URL}/ems/orgcompetitions`, {
        params: {
          competition_id: cid,
          status: "active",
          search: search || undefined,
        },
      });

      // If current active competition has no rows, fallback to LA 2028
      if (Array.isArray(res.data) && res.data.length === 0) {
        const laCid = await resolveLa2028CompetitionId();
        if (laCid && laCid !== cid) {
          const laRes = await axios.get(`${API_URL}/ems/orgcompetitions`, {
            params: {
              competition_id: laCid,
              status: "active",
              search: search || undefined,
            },
          });
          if (Array.isArray(laRes.data) && laRes.data.length > 0) {
            cid = laCid;
            setCompetitionId(laCid);
            setListContext("Showing Los Angeles 2028 list (active competition has no organisations).");
            res = laRes;
          }
        }
      }

      const olympicRows = await fetchOlympicDetails(search);
      const olympicByOrgId = new Map(
        olympicRows.map((r) => [
          String(r?.org_id?._id || r?.org_id || r?._id || ""),
          r,
        ])
      );

      let normalized = (Array.isArray(res.data) ? res.data : []).map((r) => {
        const org = r.org_id || {};
        const contactEmails = [org.email, org.email2].filter(Boolean);
        const orgId = String(org?._id || r?.org_id || "");
        const detail = olympicByOrgId.get(orgId);
        return {
          ...r,
          teamMembers: detail?.teamMembers ?? r.teamMembers ?? 0,
          emails: detail?.emails?.length ? detail.emails : contactEmails,
          tlMen: detail?.tlMen ?? Boolean(r.tlMen),
          tlWomen: detail?.tlWomen ?? Boolean(r.tlWomen),
          updatedAt: detail?.updatedAt || r.updatedAt || null,
          _fallbackOnly: false,
        };
      });

      // If no orgcompetition rows found, fallback to Olympics rows so legacy counts remain visible.
      if (normalized.length === 0 && olympicRows.length > 0) {
        normalized = olympicRows.map((r) => ({
          ...r,
          _id: String(r?.org_id?._id || r?.org_id || r?._id || ""),
          status: "active",
          _fallbackOnly: true,
        }));
        
      }

      if (gen !== fetchGenRef.current) return;
      setRows(normalized);
    } catch (err) {
      if (gen !== fetchGenRef.current) return;
      console.error("Failed to fetch participating organisations:", err);
      setListError(
        err.response?.data?.message ||
          "Could not load organisations from orgcompetitions."
      );
      setRows([]);
    } finally {
      if (gen === fetchGenRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchRows(searchTerm);
  }, [searchTerm]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((r) => {
      const org = r.org_id || {};
      return (
        String(org.name || "").toLowerCase().includes(term) ||
        String(org.email || "").toLowerCase().includes(term) ||
        String(org.country_id?.code || "").toLowerCase().includes(term) ||
        String((r.emails || []).join(" ") || "")
          .toLowerCase()
          .includes(term)
      );
    });
  }, [rows, searchTerm]);

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const sortedRows = sortConfig.key
    ? [...filteredRows].sort((a, b) => {
        const org_a = a.org_id || {};
        const org_b = b.org_id || {};
        let aVal, bVal;
        if (sortConfig.key === 'country') {
          aVal = (org_a.country_id?.code || '').toLowerCase();
          bVal = (org_b.country_id?.code || '').toLowerCase();
        } else if (sortConfig.key === 'name') {
          aVal = (org_a.name || '').toLowerCase();
          bVal = (org_b.name || '').toLowerCase();
        } else if (sortConfig.key === 'teamMembers') {
          return sortConfig.direction === 'asc'
            ? (a.teamMembers || 0) - (b.teamMembers || 0)
            : (b.teamMembers || 0) - (a.teamMembers || 0);
        } else if (sortConfig.key === 'updatedAt') {
          const aDate = new Date(a.updatedAt || 0).getTime();
          const bDate = new Date(b.updatedAt || 0).getTime();
          return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
        } else {
          return 0;
        }
        const result = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? result : -result;
      })
    : filteredRows;

  // ✅ Pagination logic
  const effectivePerPage =
    itemsPerPage >= sortedRows.length
      ? Math.max(sortedRows.length, 1)
      : itemsPerPage;

  const totalPages = Math.max(
    Math.ceil(sortedRows.length / effectivePerPage),
    1
  );

  // After a row is removed, stay on a valid page (no full-table refetch).
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const indexOfLast = currentPage * effectivePerPage;
  const indexOfFirst = indexOfLast - effectivePerPage;
  const currentRows = sortedRows.slice(indexOfFirst, indexOfLast);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleDelete = async (orgCompetitionId) => {
    if (
      !window.confirm(
        "Remove this organisation from active competition list?"
      )
    )
      return;

    const idStr = String(orgCompetitionId);
    if (deletingId) return;
    setDeletingId(idStr);
    try {
      await axios.delete(`${API_URL}/ems/orgcompetitions/${orgCompetitionId}`);
      // Drop any in-flight list fetch so it cannot overwrite this removal.
      fetchGenRef.current += 1;
      setRows((prev) => prev.filter((r) => String(r._id) !== idStr));
      setLoading(false);
    } catch (err) {
      console.error("Failed to remove organisation:", err);
      alert("Failed to remove organisation.");
    } finally {
      setDeletingId((cur) => (cur === idStr ? null : cur));
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="participating-orgs-list" style={{ marginTop: 82 }}>
      <div className="mb-3">
        {/* <h4 className="mb-1">Participating Organisations</h4> */}
        {listContext && <div className="text-muted small">{listContext}</div>}
        {listError && (
          <div className="alert alert-warning py-2 mt-2 mb-0" role="alert">
            {listError}
          </div>
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="w-25">
          <input
            className="form-control"
            type="text"
            placeholder="Search organisation"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {/* TABLE + PAGINATION INSIDE */}
      <div className="org-table-container">
        <table className="org-table">
          <thead>
            <tr className="org-header">
              <th>Logo</th>
              <th
                className={`sort-header ${sortConfig.key === 'country' ? 'sorted' : ''}`}
                onClick={() => toggleSort('country')}
              >
                Country <FontAwesomeIcon icon={sortIcon('country')} className="sort-icon" />
              </th>
              <th
                className={`sort-header ${sortConfig.key === 'name' ? 'sorted' : ''}`}
                onClick={() => toggleSort('name')}
              >
                Name <FontAwesomeIcon icon={sortIcon('name')} className="sort-icon" />
              </th>
              <th
                className={`sort-header ${sortConfig.key === 'teamMembers' ? 'sorted' : ''}`}
                onClick={() => toggleSort('teamMembers')}
              >
                No. of Team Members <FontAwesomeIcon icon={sortIcon('teamMembers')} className="sort-icon" />
              </th>
              <th>Emails</th>
              <th>TL Men</th>
              <th>TL Women</th>
              <th
                className={`sort-header ${sortConfig.key === 'updatedAt' ? 'sorted' : ''}`}
                onClick={() => toggleSort('updatedAt')}
              >
                Last Updated <FontAwesomeIcon icon={sortIcon('updatedAt')} className="sort-icon" />
              </th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="text-center p-3">
                  Loading...
                </td>
              </tr>
            ) : currentRows.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center p-3 text-muted">
                  No participating organisations found.
                </td>
              </tr>
            ) : (
              currentRows.map((row) => {
                const org = row.org_id || {};

                return (
                  <tr className="org-row" key={row._id}>
                    <td>
                      <img
                        src={
                          org.logo
                            ? `/images/${org.logo}`
                            : "/images/org_logo1.png"
                        }
                        alt="logo"
                        style={{ width: 40, height: 40, borderRadius: 6 }}
                      />
                    </td>

                    <td>{org.country_id?.code || "-"}</td>

                    <td
                      className="org-name"
                      onClick={() =>
                        navigate(`/organisation/${org._id}`, {
                          state: { organisation: org },
                        })
                      }
                    >
                      {org.name || "Unnamed Organisation"}
                    </td>

                    <td>{row.teamMembers || 0}</td>

                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span>{(row.emails || []).join(", ") || org.email || "-"}</span>
                        {(row.emails?.[0] || org.email) && (
                          <button
                            className="icon-btn"
                            onClick={() =>
                              copyToClipboard((row.emails || []).join(", ") || org.email)
                            }
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                        )}
                      </div>
                    </td>

                    <td>{row.tlMen ? "Yes" : "No"}</td>
                    <td>{row.tlWomen ? "Yes" : "No"}</td>
                    <td>
                      {row.updatedAt
                        ? new Date(row.updatedAt).toLocaleString()
                        : "-"}
                    </td>

                    <td>
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          className="icon-btn"
                          onClick={() =>
                            navigate(`/organisation/${org._id}/edit`, {
                              state: { organisation: org },
                            })
                          }
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>

                        <button
                          className="icon-btn"
                          onClick={() => handleDelete(row._id)}
                          disabled={row._fallbackOnly || Boolean(deletingId)}
                          title={
                            row._fallbackOnly
                              ? "Add in orgcompetition to enable remove."
                              : "Remove from competition"
                          }
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* ✅ PAGINATION INSIDE TABLE CONTAINER */}
        {sortedRows.length > 0 && (
          <div className="participant-pagination">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: "13px", color: "#666" }}>Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto", fontSize: "13px" }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="pagination-box">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {pageNumbers.map((num) => (
                <button
                  key={num}
                  className={`pagination-btn ${
                    num === currentPage ? "active" : ""
                  }`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}

              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipatingOrganisationsList;